import React, { useEffect, useRef, useState } from "react";
import { FaCodeCompare } from "react-icons/fa6";

import { PageTable, Header } from "/src/layouts/page";
import promptService from "/src/features/ia/services/promptService";
import storage from "/src/utils/storage";

import "./PromptTable.css";
import { useToastDispatchContext } from '/src/layouts/toast/context/toastContext';
import PromptDiff from "./PromptDiff";

function PromptCard({ prompt, currentUser, refresh }) {
    const toast = useToastDispatchContext();
    const [text, setText] = useState(prompt.template_body || "");
    const [chunkSize, setChunkSize] = useState(prompt.chunk_size ?? 0);
    const [saveStatus, setSaveStatus] = useState(false);
    const [showDiff, setShowDiff] = useState(false);

    useEffect(() => {
        setText(prompt.template_body || "");
    }, [prompt.template_body]);

    useEffect(() => {
        setChunkSize(prompt.chunk_size ?? 0);
    }, [prompt.chunk_size]);

    const handleSave = async () => {
        if (!currentUser?.is_admin) return;
        setSaveStatus("saving");

        const [result, error] = await promptService.updatePrompt(prompt.id, {
            template_body: text,
            chunk_size: chunkSize
        });
        if (error) {
            toast.error("Error", "Error while updating prompts.");
            setSaveStatus("error");
            return;
        }

        toast.success("Prompt saved", "Modifications has been saved.");
        refresh({ ...prompt, template_body: text, chunk_size: chunkSize, ...result });
        setSaveStatus("saved");
    };

    const onChange = (e) => {
        setText(e.target.value);
    };

    const onChunkChange = (valueOrEvent) => {
        let value = typeof valueOrEvent === "number" ? valueOrEvent : parseInt(valueOrEvent.target.value, 10);

        if (isNaN(value) || value < 0) value = 0;
        if (value > 999) value = 999;

        setChunkSize(value);
    };
    const inputRef = useRef(null);

    useEffect(() => {
        const input = inputRef.current;
        if (!input) return;

        const handleWheel = (e) => {
            e.preventDefault();

            if (e.deltaY < 0) {
                onChunkChange(chunkSize + 1);
            } else {
                onChunkChange(chunkSize - 1);
            }
        };

        input.addEventListener("wheel", handleWheel, { passive: false });

        return () => {
            input.removeEventListener("wheel", handleWheel);
        };
    }, [chunkSize]);

    const editable = !!currentUser?.is_admin;
    const hasLastSaved = Boolean(prompt.last_saved_prompt) && prompt.last_saved_prompt !== text;

    return (
        <div className="prompt-card">
            <div className="prompt-header">
                <div className="prompt-category">
                    {prompt.category}
                </div>
                <div className="prompt-header-actions">
                    {
                        hasLastSaved &&
                        <div
                            className={`metadata_chip diff-toggle ${showDiff ? "active" : ""}`}
                            onClick={() => setShowDiff(prev => !prev)}
                        >
                            <FaCodeCompare />&nbsp;
                            {showDiff ? "Hide changes" : "View changes"}
                        </div>
                    }
                    <div className="metadata_chip purple">
                        Chunk :&nbsp;
                        {
                            editable ?
                                <div className="chunk-control">

                                    <input ref={inputRef} type="number" inputMode="numeric" className="chunk-input" value={chunkSize} onChange={onChunkChange} />

                                    <div className="chunk-buttons">
                                        <button type="button" className="chunk-btn" onClick={() => onChunkChange({ target: { value: Math.min(chunkSize + 1, 999) } })}>
                                            ▲
                                        </button>
                                        <button type="button" className="chunk-btn" onClick={() => onChunkChange({ target: { value: Math.max(chunkSize - 1, 0) } })}>
                                            ▼
                                        </button>
                                    </div>

                                </div>
                                :
                                <span>{prompt.chunk_size}</span>
                        }
                    </div>
                </div>
            </div>
            {
                showDiff && hasLastSaved &&
                <PromptDiff oldText={prompt.last_saved_prompt} newText={text} />
            }
            {
                editable ?
                    <textarea className="prompt-input" value={text} onChange={onChange} rows={8} />
                    :
                    <div className="prompt-text">{text} </div>
            }
            {
                editable &&
                <div className="prompt-footer">
                    <div className={`save-status ${saveStatus}`}>
                        {
                            saveStatus === "saving" &&
                            <>
                                <span className="loader-dot"></span>
                                Saving...
                            </>
                        }

                        {
                            saveStatus === "saved" &&
                            <>
                                ✓ Saved
                            </>
                        }
                        {
                            saveStatus === "error" &&
                            <>
                                ⚠ Error
                            </>
                        }
                    </div>
                    <div className="button_review" onClick={handleSave}>
                        Save
                    </div>

                </div>
            }
        </div>
    );
}
function PromptTable() {
    const toast = useToastDispatchContext();

    const currentUser = storage("local").get("user");

    const [prompts, setPrompts] = useState([]);

    const loadPrompts = async () => {
        const [result, error] = await promptService.getPrompts();
        if (error) {
            toast.error("Error loading prompts", "The data could'nt be reached.");
            return;
        }
        setPrompts(result);
    };

    useEffect(() => {
        loadPrompts();
    }, []);

    const updatePrompt = (updated) => {
        setPrompts(prev => prev.map(item => item.id === updated.id ? updated : item));
    };
    const copy_value = async (value) => {
        await navigator.clipboard.writeText(value);
        toast.success("Copied", `${value} copied to clipboard.`);
    };
    return (
        <PageTable>
            <Header title="AI Prompts" />
            <div className="prompt-variables">

                <div className="metadata_chip main" onClick={() => copy_value("[VARIABLE_CRITERIA]")}>
                    <strong>[VARIABLE_CRITERIA]</strong>&nbsp;
                    <span>
                        QA criteria injected into the prompt
                    </span>
                </div>

                <div className="metadata_chip main" onClick={() => copy_value("[VARIABLE_CHUNK]")}>
                    <strong>[VARIABLE_CHUNK]</strong>&nbsp;
                    <span>
                        Transcript segments analyzed by the model
                    </span>
                </div>

            </div>
            <div className="prompt-list">
                {
                    prompt != null && prompts.map(prompt => {
                        console.log(prompt)
                        return (
                            <PromptCard key={prompt.id} prompt={prompt} currentUser={currentUser} refresh={updatePrompt} />
                        )
                    })
                }
            </div>
        </PageTable>
    );
}

export default PromptTable;